/* =============================================
   Inquiry Forms — Studio Ode (INQ-01)

   Handles:
   - Accordion toggle (single-open at a time)
   - Popup open / close (Esc, outside click, X)
   - Custom single-select dropdowns (underline + ▾)
   - Custom multi-select dropdowns (same shell, toggle options)
   - Multi-select + quantity checklist (per-option Qty)
   - Inline calendar for date fields
   - Terms checkbox
   - Submit-enable validation
   - Success state after Shopify contact form submits

   The page chrome (header, breadcrumbs) is hidden via
   `body.ode-inq-open` — same pattern as the bespoke quote popup.
   ============================================= */

(function () {
  'use strict';

  var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function formatDate(d) {
    return d.getDate() + ' ' + MONTHS[d.getMonth()].slice(0, 3) + ' ' + d.getFullYear();
  }

  function isoDate(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  /* =========================================================================
     DROPDOWN (single + multi)
     ========================================================================= */
  function initDropdown(root) {
    var mode = root.dataset.mode || 'single';
    var trigger = root.querySelector('.ode-inq__dropdown-trigger');
    var menu = root.querySelector('.ode-inq__dropdown-menu');
    var textEl = root.querySelector('.ode-inq__dropdown-text');
    var hiddenInput = root.querySelector('[data-ode-inq-value]');
    var options = Array.prototype.slice.call(root.querySelectorAll('.ode-inq__dropdown-option'));
    if (!trigger || !menu || !textEl || !hiddenInput) return;

    var placeholder = textEl.textContent;

    function open() {
      closeAllDropdowns(root);
      trigger.setAttribute('aria-expanded', 'true');
      menu.classList.add('is-open');
    }
    function close() {
      trigger.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
    }
    function toggle() {
      if (menu.classList.contains('is-open')) close();
      else open();
    }

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    });

    options.forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        e.stopPropagation();
        var value = opt.dataset.value;

        if (mode === 'single') {
          options.forEach(function (o) { o.classList.remove('is-selected'); });
          opt.classList.add('is-selected');
          hiddenInput.value = value;
          textEl.textContent = value;
          textEl.classList.remove('ode-inq__dropdown-text--placeholder');
          close();
        } else {
          /* multi */
          var selected = opt.getAttribute('aria-selected') === 'true';
          opt.setAttribute('aria-selected', selected ? 'false' : 'true');
          var picked = options
            .filter(function (o) { return o.getAttribute('aria-selected') === 'true'; })
            .map(function (o) { return o.dataset.value; });
          hiddenInput.value = picked.join(', ');
          if (picked.length > 0) {
            textEl.textContent = picked.join(', ');
            textEl.classList.remove('ode-inq__dropdown-text--placeholder');
          } else {
            textEl.textContent = placeholder;
            textEl.classList.add('ode-inq__dropdown-text--placeholder');
          }
        }

        /* Fire change so form validation re-runs */
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      opt.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          opt.click();
        }
      });
    });

    /* Outside click closes */
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });

    root.__odeInqDropdownClose = close;
  }

  function closeAllDropdowns(except) {
    document.querySelectorAll('.ode-inq__dropdown').forEach(function (d) {
      if (d !== except && typeof d.__odeInqDropdownClose === 'function') d.__odeInqDropdownClose();
    });
  }

  /* =========================================================================
     MULTI-SELECT + QUANTITY (checklist)
     ========================================================================= */
  function initQtyList(root) {
    var hiddenInput = root.querySelector('[data-ode-inq-value]');
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-ode-inq-qty-row]'));
    if (!hiddenInput || !rows.length) return;

    function sync() {
      var parts = [];
      rows.forEach(function (row) {
        var option = row.getAttribute('data-ode-inq-qty-option');
        var qtyInput = row.querySelector('[data-ode-inq-qty-input]');
        if (!option || !qtyInput) return;

        var raw = (qtyInput.value || '').trim();
        var qty = parseInt(raw, 10);
        if (raw !== '' && qty >= 1) {
          parts.push(option + ' × ' + qty);
          row.classList.add('is-filled');
        } else {
          row.classList.remove('is-filled');
        }
      });

      hiddenInput.value = parts.join(', ');
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    rows.forEach(function (row) {
      var qtyInput = row.querySelector('[data-ode-inq-qty-input]');
      if (!qtyInput) return;
      qtyInput.addEventListener('input', sync);
      qtyInput.addEventListener('change', sync);
    });

    sync();
  }

  /* =========================================================================
     CALENDAR / DATE PICKER
     ========================================================================= */
  function initDatePicker(root) {
    var input = root.querySelector('.ode-inq__input--date');
    var cal = root.querySelector('.ode-inq__calendar');
    var titleEl = root.querySelector('.ode-inq__cal-title');
    var daysEl = root.querySelector('.ode-inq__cal-days');
    var navBtns = root.querySelectorAll('.ode-inq__cal-nav');
    if (!input || !cal || !titleEl || !daysEl) return;

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var viewYear = today.getFullYear();
    var viewMonth = today.getMonth();
    var selected = null;

    function render() {
      titleEl.textContent = MONTHS[viewMonth] + ' ' + viewYear;
      daysEl.innerHTML = '';

      var firstDay = new Date(viewYear, viewMonth, 1).getDay();
      var lastDate = new Date(viewYear, viewMonth + 1, 0).getDate();

      for (var i = 0; i < firstDay; i++) {
        var pad = document.createElement('span');
        pad.className = 'ode-inq__cal-day is-empty';
        daysEl.appendChild(pad);
      }

      for (var day = 1; day <= lastDate; day++) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ode-inq__cal-day';
        btn.textContent = day;
        var thisDate = new Date(viewYear, viewMonth, day);
        thisDate.setHours(0, 0, 0, 0);

        if (thisDate < today) {
          btn.disabled = true;
        }
        if (selected && isoDate(thisDate) === isoDate(selected)) {
          btn.classList.add('is-selected');
        }

        (function (d) {
          btn.addEventListener('click', function () {
            selected = d;
            input.value = formatDate(d);
            input.dataset.isoValue = isoDate(d);
            input.setAttribute('aria-expanded', 'false');
            cal.classList.remove('is-open');
            input.dispatchEvent(new Event('change', { bubbles: true }));
            render();
          });
        })(thisDate);

        daysEl.appendChild(btn);
      }
    }

    function open() {
      closeAllCalendars(root);
      cal.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
      render();
    }
    function close() {
      cal.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
    }

    input.addEventListener('click', function (e) {
      e.stopPropagation();
      if (cal.classList.contains('is-open')) close();
      else open();
    });

    navBtns.forEach(function (nav) {
      nav.addEventListener('click', function (e) {
        e.stopPropagation();
        var dir = parseInt(nav.dataset.dir, 10) || 0;
        viewMonth += dir;
        if (viewMonth < 0) { viewMonth = 11; viewYear--; }
        else if (viewMonth > 11) { viewMonth = 0; viewYear++; }
        render();
      });
    });

    /* Outside click closes */
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });

    root.__odeInqDateClose = close;
  }

  function closeAllCalendars(except) {
    document.querySelectorAll('[data-ode-inq-date]').forEach(function (d) {
      if (d !== except && typeof d.__odeInqDateClose === 'function') d.__odeInqDateClose();
    });
  }

  /* =========================================================================
     POPUP + ACCORDION + VALIDATION per section root
     ========================================================================= */
  function initRoot(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.ode-inq__tab'));
    var inquireBtns = Array.prototype.slice.call(root.querySelectorAll('.ode-inq__inquire-btn'));

    /* ── ACCORDION ── */
    tabs.forEach(function (tab) {
      var header = tab.querySelector('.ode-inq__tab-header');
      if (!header) return;
      header.addEventListener('click', function () {
        var wasOpen = tab.classList.contains('is-open');
        tabs.forEach(function (t) {
          t.classList.remove('is-open');
          var h = t.querySelector('.ode-inq__tab-header');
          if (h) h.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          tab.classList.add('is-open');
          header.setAttribute('aria-expanded', 'true');
        }
      });
    });

    /* ── POPUP OPEN / CLOSE ── */
    /* Popups render OUTSIDE root.ode-inquiry-forms (as siblings in the shopify-section),
       so query from the parent .shopify-section for scoped matching. */
    var sectionHost = root.closest('.shopify-section') || document;

    /* Accordion Inquire buttons — also covered by global [data-opens-form] delegation.
       Keep direct listeners so Theme Editor section reloads stay snappy. */
    inquireBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var groupId = btn.dataset.opensForm;
        var popup = sectionHost.querySelector('.ode-inq__popup-overlay[data-form="' + groupId + '"]')
          || document.querySelector('.ode-inq__popup-overlay[data-form="' + groupId + '"]');
        if (popup) openPopup(popup);
      });
    });

    var popups = Array.prototype.slice.call(sectionHost.querySelectorAll('.ode-inq__popup-overlay'));
    popups.forEach(function (popup) {
      var close = popup.querySelector('.ode-inq__popup-close');
      if (close) {
        close.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closePopup(popup);
        });
      }

      /* Success-state "Close" button (shown after a successful submit). */
      var successClose = popup.querySelector('.ode-inq__success-close');
      if (successClose) {
        successClose.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closePopup(popup);
        });
      }

      popup.addEventListener('click', function (e) {
        if (e.target === popup) closePopup(popup);
      });

      /* ── FIELDS inside this popup ── */
      initPopupFields(popup);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        popups.forEach(function (p) {
          if (p.classList.contains('is-open')) closePopup(p);
        });
      }
    });
  }

  /* External triggers (Studio/Work hub CTAs, bridal mock, etc.) —
     any element with data-opens-form anywhere on the page. */
  function bindGlobalOpenTriggers() {
    if (document.__odeInqGlobalOpen) return;
    document.__odeInqGlobalOpen = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-opens-form]');
      if (!btn) return;
      /* Accordion inquire buttons already have their own handler */
      if (btn.classList.contains('ode-inq__inquire-btn')) return;
      var groupId = btn.getAttribute('data-opens-form');
      if (!groupId) return;
      var popup = document.querySelector('.ode-inq__popup-overlay[data-form="' + groupId + '"]');
      if (!popup) return;
      e.preventDefault();
      openPopup(popup);
    });
  }

  function openPopup(popup) {
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ode-inq-open');
    document.body.style.overflow = 'hidden';

    /* Check if we just successfully submitted this form (via Shopify redirect
       back with form.posted_successfully?). If so, show success state. */
    var successFlag = popup.querySelector('[data-ode-inq-success]');
    var formState = popup.querySelector('.ode-inq__form-state');
    var successState = popup.querySelector('.ode-inq__success-state');
    if (successFlag && formState && successState) {
      formState.classList.add('is-hidden');
      successState.classList.add('is-visible');
    }

    /* Autofocus first input */
    setTimeout(function () {
      var first = popup.querySelector('input:not([type="hidden"]), textarea, .ode-inq__dropdown-trigger');
      if (first && typeof first.focus === 'function') first.focus();
    }, 60);
  }

  function closePopup(popup) {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ode-inq-open');
    document.body.style.overflow = '';

    /* Reset success state + drop the success flag so the popup shows the
       form again next time it's opened. The submission is already saved
       server-side; this only affects what's rendered in the popup shell. */
    var formState = popup.querySelector('.ode-inq__form-state');
    var successState = popup.querySelector('.ode-inq__success-state');
    if (formState) formState.classList.remove('is-hidden');
    if (successState) successState.classList.remove('is-visible');
    var flag = popup.querySelector('[data-ode-inq-success]');
    if (flag && flag.parentNode) flag.parentNode.removeChild(flag);

    /* Clear any URL hash/query from Shopify form redirect so reopening
       doesn't immediately show success state again. */
    if (typeof history !== 'undefined' && history.replaceState) {
      var url = window.location.pathname + window.location.search;
      if (window.location.hash) history.replaceState(null, '', url);
    }
  }

  function initPopupFields(popup) {
    /* Dropdowns */
    popup.querySelectorAll('[data-ode-inq-dropdown]').forEach(initDropdown);

    /* Multi-select + quantity checklists */
    popup.querySelectorAll('[data-ode-inq-qty-list]').forEach(initQtyList);

    /* Date pickers */
    popup.querySelectorAll('[data-ode-inq-date]').forEach(initDatePicker);

    /* Terms checkbox */
    var terms = popup.querySelectorAll('.ode-inq__terms');
    terms.forEach(function (row) {
      var checkbox = row.querySelector('.ode-inq__checkbox');
      if (!checkbox) return;
      function toggle() {
        var checked = checkbox.getAttribute('aria-checked') === 'true';
        checkbox.setAttribute('aria-checked', String(!checked));
        validate(popup);
      }
      row.addEventListener('click', function (e) {
        /* Avoid double-toggle when clicking a link inside the terms */
        if (e.target && e.target.closest('a')) return;
        toggle();
      });
      checkbox.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    });

    /* Validation: watch every field for change */
    var trackables = popup.querySelectorAll('input, textarea');
    trackables.forEach(function (el) {
      el.addEventListener('input', function () { validate(popup); });
      el.addEventListener('change', function () { validate(popup); });
    });

    /* Initial pass */
    validate(popup);
  }

  function validate(popup) {
    var submitBtn = popup.querySelector('.ode-inq__submit');
    if (!submitBtn) return;

    var valid = true;

    var requiredFields = popup.querySelectorAll('input[required], textarea[required]');
    requiredFields.forEach(function (el) {
      var val = (el.value || '').trim();
      if (!val) valid = false;
    });

    var checkbox = popup.querySelector('.ode-inq__checkbox');
    if (checkbox && checkbox.getAttribute('aria-checked') !== 'true') {
      valid = false;
    }

    submitBtn.disabled = !valid;
  }

  /* =========================================================================
     BOOT
     ========================================================================= */
  /* One-shot guard — auto-open the success popup at most once per page load.
     Without this, theme-editor section reloads (shopify:section:load) re-run
     boot() and re-render fresh HTML that still carries the success flag,
     causing the popup to reopen immediately after the user closes it. */
  var autoOpenedForSuccess = false;

  function boot() {
    bindGlobalOpenTriggers();

    document.querySelectorAll('.ode-inquiry-forms[data-inquiry-root]').forEach(function (root) {
      if (root.__odeInqInit) return;
      root.__odeInqInit = true;
      initRoot(root);
    });

    if (autoOpenedForSuccess) return;

    /* After Shopify reloads the page following a successful contact form
       submission, the corresponding popup carries <input data-ode-inq-success>.
       Auto-reopen it so the user sees the success state. openPopup() swaps
       form → success state when the flag is present. */
    document.querySelectorAll('.ode-inq__popup-overlay').forEach(function (popup) {
      if (autoOpenedForSuccess) return;
      if (popup.querySelector('[data-ode-inq-success]')) {
        autoOpenedForSuccess = true;
        openPopup(popup);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init on Shopify section reload (theme editor) */
  document.addEventListener('shopify:section:load', boot);
})();
